import { trigger, state, style, transition, animate } from '@angular/animations';
import { DomSanitizer } from '@angular/platform-browser';
import { MatDialog } from '@angular/material/dialog';
import { MessageTemplateEditComponent } from './dialogs/message-template-edit.component';
import { MessageTemplateEntityService } from './services/message-template-entity.service';
import { MessageTemplateDataService } from './services/message-template-data.service';
import { map, exhaustMap, tap } from 'rxjs/operators';
import { MessageTemplate } from '@core/models/message-template.model';
import { Observable, of, Subscription } from 'rxjs';
import { LoadingBarService } from '@ngx-loading-bar/core';
import { Pagination } from '@core/models/pagination.model';
import { DropdownHttpService } from '@core/services/dropdown-http.service';
import { FormGroup, FormControl } from '@angular/forms';
import { Component, OnInit, OnDestroy } from '@angular/core';
import { AppPermissionService } from '@core/services/app-permission.service';
import { AfffiliateMessageTemplateDataService } from './services/message-template-affiliate-data.service';
import { EventEmitterService } from '@core/services/event-emitter.service';

@Component({
  selector: 'kt-message-template',
  templateUrl: './message-template.component.html',
  styleUrls: ['./message-template.component.scss'],
  animations: [
    trigger('panelState', [
      state('closed', style({ height: '80px', overflow: 'hidden' })),
      state('open', style({ height: '*' })),
      transition('closed <=> open', animate('300ms ease-in-out'))
    ])
  ]
})
export class MessageTemplateComponent implements OnInit, OnDestroy {

  form: FormGroup;
  pagination: Pagination;
  pageSize = 30;
  page = 1;
  maxSize = 5;
  params = '';

  messageTemplates$: Observable<MessageTemplate[]>;
  dropdown = {
    statuses: this.dropdownHttpService.statuses,
    locales: [],
    perPage: this.dropdownHttpService.perPage,
    type: this.dropdownHttpService.messageTemplateType,
    section: this.dropdownHttpService.messageTemplateSection()
  };
  searchLocale = [];
  searchType = [1, 2, 3];
  loading = false;
  clearBtnLoading = false;
  searchBtnLoading = false;
  dataLength = 0;
  affiliatePendingCount = 0;

  // permissions
  canViewMessageTemplateList: boolean;
  canViewAffiliateMessageTemplateList: boolean;

  private localesSub = new Subscription();
  private subscription = new Subscription();
  private refreshSubcription = new Subscription();
  private subscriptions = new Subscription();
  constructor(
    private dropdownHttpService: DropdownHttpService,
    private messageTemplateDataService: MessageTemplateDataService,
    private affiliateMessageTemplateDataService: AfffiliateMessageTemplateDataService,
    private messageTemplateEntityService: MessageTemplateEntityService,
    public loadingBar: LoadingBarService,
    public dialog: MatDialog,
    public sanitizer: DomSanitizer,
    private appPermissionService: AppPermissionService,
    private eventEmitterService: EventEmitterService,
  ) { }

  ngOnInit() {
    this.formInit();
    this.pagination = this.messageTemplateDataService.pagination;
    this.setLocales();

    this.getPendingCount();

    this.eventEmitterService.updateAffiliateMessageTemplateTabCountVar = this.eventEmitterService.updateAffiliateMessageTemplateTabCount.subscribe(() => {
      this.getPendingCount();
    });

    const apSub = this.appPermissionService.getAppPermissions().subscribe(appPermissions => {
      this.canViewMessageTemplateList = appPermissions.view_message_template_list;
      this.canViewAffiliateMessageTemplateList = appPermissions.view_affiliate_message_template_list;
    });

    this.subscriptions.add(apSub);
  }

  ngOnDestroy() {
    this.subscription.unsubscribe();
    this.subscriptions.unsubscribe();
    this.refreshSubcription.unsubscribe();
    this.localesSub.unsubscribe();
  }

  getPendingCount() {
    this.affiliateMessageTemplateDataService.getPendingCount().subscribe(res => {
      this.affiliatePendingCount = res;
    });
  }

  onViewPageBy(page = 1, pageSize?: number, params?: string) {
    this.loading = true;
    pageSize = this.pageSize;
    params = this.params ? `&${this.params}` : '';
    this.loadingBar.start();
    return this.messageTemplates$ = this.messageTemplateEntityService.getWithQuery(`?page=${page}&perPage=${pageSize}${params}`).pipe(
      tap(res => {
        this.pagination = this.messageTemplateDataService.pagination;
        this.loading = false;
        this.dataLength = res.length;
        this.loadingBar.complete();
      })
    );
  }

  onPerPage(size: Event) {
    this.pageSize = +(size.target as HTMLSelectElement).value;
    this.onViewPageBy(this.page, this.pageSize, this.params);
  }

  onSubmit(clearSearch?: boolean) {
    this.searchBtnLoading = clearSearch ? false : true;
    this.loading = true;
    of(this.form.value).pipe(
      map(this.filterFormFields),
      exhaustMap((data) => {
        const localeCheckboxParams = this.generateCheckboxParams('locale');
        const typeCheckboxParams = this.generateCheckboxParams('type');
        const paramsRef = Object.keys(data).map(key => key + '=' + data[key]).join('&');
        this.params = paramsRef.replace(localeCheckboxParams.oldCheckboxParams, localeCheckboxParams.newCheckboxParams);
        this.params = this.params.replace(typeCheckboxParams.oldCheckboxParams, typeCheckboxParams.newCheckboxParams);
        this.loadingBar.start();
        return this.messageTemplates$ = this.messageTemplateEntityService.getWithQuery(`?${this.params}&perPage=${this.pageSize}`).pipe(
          tap(res => {
            this.page = 1;
            this.loading = false;
            this.clearBtnLoading = false;
            this.searchBtnLoading = false;
            this.dataLength = res.length;
            this.pagination = this.messageTemplateDataService.pagination;
            this.loadingBar.complete();
          })
        );
      }),
    ).subscribe();
  }

  onClear() {
    this.clearBtnLoading = true;
    this.searchType = [1, 2, 3];
    this.searchLocale = [];
    this.dropdown.type.forEach(val => {
      val.checked = true;
    });
    this.dropdown.locales.forEach(val => {
      this.searchLocale.push(val.id);
      val.checked = true;
    });
    this.formInit();
    this.onSubmit(true);
  }

  onCheckBoxFilter(event: any, id: number, type: string, ind?: number) {
    if (type === 'locale') {
      const position = this.searchLocale.indexOf(id);
      if (event.target.checked) {
        this.searchLocale.push(id);
        this.dropdown.locales[ind].checked = true;
      } else {
        this.searchLocale.splice(position, 1);
        this.dropdown.locales[ind].checked = false;
      }
      this.form.patchValue({ settings_locale_id: this.searchLocale });
    } else {
      const position = this.searchType.indexOf(id);
      if (event.target.checked) {
        this.searchType.push(id);
        this.dropdown.type[ind].checked = true;
      } else {
        this.searchType.splice(position, 1);
        this.dropdown.type[ind].checked = false;
      }
      this.form.patchValue({ type: this.searchType });
    }
  }

  onOpenDialog(type: string, row?: any) {
    if (row) {
      this.messageTemplateDataService.getById(row.id).subscribe(res => {
        this.openDialogBy(MessageTemplateEditComponent, { messageTemplate: res, mode: type });
      });
    } else {
      this.openDialogBy(MessageTemplateEditComponent, { mode: type });
    }
  }

  onContent(content) {
    return this.sanitizer.bypassSecurityTrustHtml(content.replace(/\&lt;/g, '<').replace(/\&gt;/g, '>'));
  }

  private openDialogBy(componentRef: any, data?: { messageTemplate?: any, mode?: any }) {
    const dialogRef = this.dialog.open(componentRef, {
      width: '800px',
      data: {
        messageTemplate: data.messageTemplate,
        mode: data.mode
      }
    });
    dialogRef.afterClosed().subscribe((result) => {
      if (result === true) {
        this.onViewPageBy(this.page).subscribe();
      }
    });
  }

  private setLocales() {
    this.localesSub = this.dropdownHttpService.locales.pipe(tap(res => {
      this.messageTemplateDataService.locales$.next(res);
      res.map(row => {
        this.searchLocale.push(row.id);
        this.dropdown.locales.push({
          id: row.id,
          name: row.name,
          code: row.code,
          checked: true
        });
      });
      this.onSubmit();
    })).subscribe();
  }

  private generateCheckboxParams(type: string) {
    if (type === 'locale') {
      return {
        newCheckboxParams: Object.keys(this.searchLocale).map(key => `settings_locale_id[${key}]=${this.searchLocale[key]}`).join('&'),
        oldCheckboxParams: 'settings_locale_id=' + Object.keys(this.searchLocale).map(key => this.searchLocale[key]).join(','),
      };
    } else {
      return {
        newCheckboxParams: Object.keys(this.searchType).map(key => `type[${key}]=${this.searchType[key]}`).join('&'),
        oldCheckboxParams: 'type=' + Object.keys(this.searchType).map(key => this.searchType[key]).join(','),
      };
    }
  }

  private filterFormFields(formData: any) {
    formData.type = formData.type.length > 0 ? formData.type : null;
    formData.settings_locale_id = formData.settings_locale_id.length > 0 ? formData.settings_locale_id : null;
    const fields = {};
    Object.keys(formData).forEach(key => (formData[key] !== '' && formData[key] !== null) ? fields[key] = formData[key] : key);
    return fields;
  }

  private formInit() {
    this.form = new FormGroup({
      code: new FormControl(null),
      message: new FormControl(null),
      section: new FormControl('all'),
      type: new FormControl(this.searchType),
      status: new FormControl(1),
      settings_locale_id: new FormControl(this.searchLocale)
    });
  }

}
